import { useEffect, useMemo, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { siteSchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import { useCreateSiteMutation, useGetOrgansQuery } from "../../services/api";
import { SingleSelectInput } from "../../components/SelectInput";
import style from "./site.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { getErrorMessage } from "../../utils/common";

type FormState = typeof siteSchema.__outputType;
type OrganOption = {
  id: number;
  label: string;
  value: number;
};

/**
 * Add Site page for creating a new site.
 *
 * Notes:
 * - A site belongs to exactly one organ, selected from the organ list.
 * - Validation is enforced via `siteSchema`.
 */
const AddSite = () => {
  const [createSite] = useCreateSiteMutation();
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const { data: organData } = useGetOrgansQuery({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: false,
    search_key: "",
  });

  const organs = useMemo<OrganOption[]>(
    () =>
      organData?.items?.map((organ: Organ) => {
        return {
          id: organ?.id,
          label: organ?.name,
          value: organ?.id,
        };
      }) ?? [],
    [organData]
  );
  const navigation = useNavigate();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(siteSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormState>({
    resolver: yupResolver(siteSchema),
    mode: "onChange",
  });

  const selectedOrganId = watch("organ_id");

  /**
   * Submits the new site payload.
   *
   * Args:
   *   values (FormState): Form values for the new site.
   *
   * Returns:
   *   Promise<void>
   *
   * Raises:
   *   Error: Propagated when the create mutation fails.
   */
  const onSubmit = async (values: FormState) => {
    try {
      setIsLoading(true);
      const { name, organ_id } = values;
      const payload: AddSitePayload = {
        name: name,
        organ_id: organ_id,
      };

      await createSite(payload).unwrap();
      setIsLoading(false);
      setMessage({
        type: "success",
        text: "Site added successfully.",
      });
      navigationTimeoutRef.current = setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Please enter valid details"),
      });
      setIsLoading(false);
    }
  };

  return (
    <Box className={style.container}>
      <Typography className={style.heading}>Add Site</Typography>
      <Typography className={style.description}>
        Enter the below details to start adding a new site to Pegasus
        Cybersoft.
      </Typography>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Box className={style.flexTop}>
          <Box className={style.row}>
            <Input
              placeholder="e.g Upper Lobe"
              label="Name"
              required
              register={register}
              name="name"
              errors={errors}
            />
          </Box>
          <Box className={style.row}>
            <SingleSelectInput
              placeholder="Select organ"
              label="Organ"
              required
              register={register}
              name="organ_id"
              errors={errors}
              data={organs}
              value={selectedOrganId ?? ""}
            />
          </Box>
        </Box>
        {message && (
          <Box mt={2}>
            <LabelBox
              boxProps={{ height: 50, display: "flex", alignItems: "center" }}
              data={message}
              onClose={() => setMessage(null)}
            />
          </Box>
        )}
        <Box className={style.buttons}>
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: "transparent" },
            }}
            variant="outlined"
            onClick={() => navigation(-1)}
            label={"Cancel"}
          />
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: colors.primary },
            }}
            variant="contained"
            type="submit"
            label={"Save"}
            loading={isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default AddSite;
